// DROP INDEX structure extractor. Walks the leading clause to capture the
// index name, optional `IF EXISTS` flag, and (when the source supplies it)
// the `ON <table>` clause that mssql/mysql require.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      ALTER TABLE / Index forms. Cross-slice acceptance matrix line for
//      drop-index documents the mssql `ON <table>` requirement.

import type { QualifiedName, StatementInfo } from '../../../sql-grammar/model';
import { readQualifiedName } from '../../scope/identifiers';
import type { DdlStructure, ParsedBuffer } from '../../types';

import { skipWsCommentsLocal, tryConsumeKeyword } from './shared';

function emptyQn(): QualifiedName {
  return { name: '', raw: '', quoted: false, foldedName: '' };
}

function fallback(stmt: StatementInfo): DdlStructure {
  return { kind: 'drop-index', range: stmt.range, indexName: emptyQn(), ifExists: false };
}

export function extractDropIndex(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const { source, options } = buffer.parsedDocument;
  const stmtStart = statementInfo.range.startOffset;
  const stmtEnd = statementInfo.range.endOffset;

  let i = skipWsCommentsLocal(source, stmtStart, stmtEnd, options);
  const afterDrop = tryConsumeKeyword(source, i, stmtEnd, options, 'DROP');
  if (afterDrop === i) return fallback(statementInfo);
  i = skipWsCommentsLocal(source, afterDrop, stmtEnd, options);

  const afterIndex = tryConsumeKeyword(source, i, stmtEnd, options, 'INDEX');
  if (afterIndex === i) return fallback(statementInfo);
  i = skipWsCommentsLocal(source, afterIndex, stmtEnd, options);

  // Optional `CONCURRENTLY` (Postgres) — preserved-only flag in v1; skip.
  const afterConc = tryConsumeKeyword(source, i, stmtEnd, options, 'CONCURRENTLY');
  if (afterConc !== i) i = skipWsCommentsLocal(source, afterConc, stmtEnd, options);

  let ifExists = false;
  const afterIf = tryConsumeKeyword(source, i, stmtEnd, options, 'IF');
  if (afterIf !== i) {
    let j = skipWsCommentsLocal(source, afterIf, stmtEnd, options);
    const afterExists = tryConsumeKeyword(source, j, stmtEnd, options, 'EXISTS');
    if (afterExists !== j) {
      ifExists = true;
      j = skipWsCommentsLocal(source, afterExists, stmtEnd, options);
    }
    i = j;
  }

  const idxName = readQualifiedName(source, i, stmtEnd, options);
  if (!idxName) return fallback(statementInfo);
  i = skipWsCommentsLocal(source, idxName.range.endOffset, stmtEnd, options);

  // Optional `ON <table>` (mssql / mysql).
  let table: QualifiedName | undefined;
  const afterOn = tryConsumeKeyword(source, i, stmtEnd, options, 'ON');
  if (afterOn !== i) {
    const after = skipWsCommentsLocal(source, afterOn, stmtEnd, options);
    const tableName = readQualifiedName(source, after, stmtEnd, options);
    if (tableName) table = tableName.qualified;
  }

  return {
    kind: 'drop-index',
    range: statementInfo.range,
    indexName: idxName.qualified,
    ifExists,
    ...(table ? { table } : {}),
  };
}
